import { quoteReviewLinkQuery } from "@common/api/quoting/review"
import { useTrackEvent } from "@common/hooks/use-track-event"
import { getErrorMessage } from "@common/lib/error-utils"
import { usePostHog } from "@posthog/react"
import {
  createFileRoute,
  type ErrorComponentProps,
  Outlet,
} from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"
import { z } from "zod"
import { Gate } from "./-components/gate"
import { Portal } from "./-components/portal"
import { ReviewSessionContext, useStoredSession } from "./-lib/session"
import { REVIEW_TABS } from "./-lib/tabs"
import { type PortalView, PortalViewContext } from "./-lib/view"

const searchSchema = z.object({
  tab: z.enum(REVIEW_TABS).optional().catch(undefined),
  task: z.string().optional().catch(undefined),
})

export const Route = createFileRoute("/review/$token")({
  validateSearch: searchSchema,
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(quoteReviewLinkQuery({ token: params.token })),
  errorComponent: LinkUnavailable,
  component: ReviewPage,
})

function useRouteView(): PortalView {
  const { tab, task } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [asking, setAsking] = useState(false)

  return {
    preview: false,
    tab: tab ?? "tasks",
    taskId: task,
    asking,
    showTab: (next) =>
      navigate({
        search: (previous) => ({
          ...previous,
          tab: next === "tasks" ? undefined : next,
        }),
      }),
    showTask: (taskId, ask = false) => {
      setAsking(ask)
      navigate({ search: (previous) => ({ ...previous, task: taskId }) })
    },
  }
}

function ReviewPage(): React.ReactElement {
  const { token } = Route.useParams()
  const link = Route.useLoaderData()
  const [session, setSession] = useStoredSession(token)
  const view = useRouteView()
  const posthog = usePostHog()
  const trackEvent = useTrackEvent()
  const tracked = useRef(false)

  useEffect(() => {
    posthog.register({
      tenant_id: link.tenant_id,
      tenant_slug: link.tenant_slug,
      quote_id: link.quote_id,
      revision_id: link.revision_id,
    })
    posthog.group("company", link.tenant_id, {
      name: link.shop.name,
      slug: link.tenant_slug,
    })
    if (!tracked.current) {
      tracked.current = true
      posthog.capture("$pageview", {
        tenant_id: link.tenant_id,
        tenant_slug: link.tenant_slug,
        quote_id: link.quote_id,
        revision_id: link.revision_id,
        review_context_ready: true,
        route: "/review/:token",
        $current_url: `${window.location.origin}/review/:token`,
      })
      trackEvent("scope_link_opened", {
        tenant_id: link.tenant_id,
        quote_id: link.quote_id,
        revision_id: link.revision_id,
        revision_number: link.revision_number,
        quote_kind: link.kind,
      })
    }
    return () => {
      posthog.unregister("tenant_id")
      posthog.unregister("tenant_slug")
      posthog.unregister("quote_id")
      posthog.unregister("revision_id")
      posthog.resetGroups()
    }
  }, [link, posthog, trackEvent])

  if (session === null) {
    return <Gate token={token} onVerified={setSession} />
  }
  return (
    <ReviewSessionContext.Provider
      value={{ token, session, signOut: () => setSession(null) }}
    >
      <PortalViewContext.Provider value={view}>
        <Portal />
        <Outlet />
      </PortalViewContext.Provider>
    </ReviewSessionContext.Provider>
  )
}

function LinkUnavailable({ error }: ErrorComponentProps): React.ReactElement {
  return (
    <div className="flex min-h-dvh items-center justify-center p-6 text-body-medium text-muted-foreground">
      {getErrorMessage(error)}
    </div>
  )
}
